import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Clock, Briefcase, BadgeCheck, Building2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { BrutalButton } from '@/components/ui/BrutalButton';
import { JobPosting, contractTypes, workModes } from '@/data/jobOptions';
import { formatDistanceToNow } from 'date-fns';
import { it, enUS, es } from 'date-fns/locale';

interface JobCardProps {
  job: JobPosting;
}

export function JobCard({ job }: JobCardProps) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  const locale = i18n.language === 'it' ? it : i18n.language === 'es' ? es : enUS;
  
  const isNew = job.published_at && 
    new Date(job.published_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

  const formatSalary = () => {
    if (!job.salary_range_min && !job.salary_range_max) return null;
    
    const formatNum = (num: number) => {
      if (num >= 1000) return `${(num / 1000).toFixed(0)}k`;
      return num.toString();
    };

    if (job.salary_range_min && job.salary_range_max) {
      return `€${formatNum(job.salary_range_min)} - €${formatNum(job.salary_range_max)}`;
    }
    if (job.salary_range_min) return `da €${formatNum(job.salary_range_min)}`;
    if (job.salary_range_max) return `fino a €${formatNum(job.salary_range_max)}`;
    return null;
  };

  const salary = formatSalary();
  const contractLabel = contractTypes.find(c => c.value === job.contract_type);
  const workModeLabel = workModes.find(w => w.value === job.work_mode);

  return (
    <div
      onClick={() => navigate(`/jobs/${job.slug}`)}
      className="bg-card border-[3px] border-dark shadow-[6px_6px_0px_0px] shadow-dark hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0px_0px] transition-all duration-200 p-6 cursor-pointer h-full flex flex-col"
    >
      <div className="flex-1">
        {/* 1. Header - Titolo + Badge temporale */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-heading text-lg font-bold text-foreground line-clamp-2">
            {job.title}
          </h3>
          {isNew && (
            <Badge className="bg-amber-500 text-dark text-[10px] font-bold uppercase px-1.5 py-0.5 border-0 shrink-0">
              {t('jobs.card.new')}
            </Badge>
          )}
        </div>

        {/* 2. Località - Icona + Città • Modalità */}
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
          <MapPin className="w-4 h-4 shrink-0" />
          <span>
            {job.location_city}
            {workModeLabel && ` • ${t(workModeLabel.labelKey)}`}
          </span>
        </div>

        {/* 3. Contratto - Icona + Tipo • Salario */}
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
          <Briefcase className="w-4 h-4 shrink-0" />
          <span>
            {contractLabel && t(contractLabel.labelKey)}
            {salary && ` • ${salary}`}
          </span>
        </div>

        {/* 4. Tempo - Icona + Quanto tempo fa */}
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-3">
          <Clock className="w-4 h-4 shrink-0" />
          <span>
            {job.published_at && formatDistanceToNow(new Date(job.published_at), { addSuffix: true, locale })}
          </span>
        </div>

        {/* 5. Badges Inclusività */}
        {job.inclusivity_tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {job.inclusivity_tags.slice(0, 3).map(tag => (
              <Badge key={tag} variant="secondary" className="text-[10px]">
                {t(`jobs.inclusivityTags.${tag}`)}
              </Badge>
            ))}
            {job.inclusivity_tags.length > 3 && (
              <Badge variant="secondary" className="text-[10px]">
                +{job.inclusivity_tags.length - 3}
              </Badge>
            )}
          </div>
        )}

        {/* 6. Footer - Logo Azienda + Nome */}
        <div className="flex items-center gap-3 pt-3 border-t-[2px] border-dark/20">
          {job.companies?.logo_url ? (
            <img 
              src={job.companies.logo_url} 
              alt={job.companies.name}
              className="w-9 h-9 border-[3px] border-dark object-cover rounded-full"
            />
          ) : (
            <div className="w-9 h-9 border-[3px] border-dark bg-muted flex items-center justify-center rounded-full">
              <Building2 className="w-4 h-4 text-muted-foreground" />
            </div>
          )}
          <span className="font-body text-sm text-foreground font-bold flex items-center gap-1">
            {job.companies?.name}
            {job.companies?.is_verified && (
              <BadgeCheck className="w-4 h-4 text-primary" />
            )}
          </span>
        </div>
      </div>

      {/* Bottone Vedi dettagli */}
      <BrutalButton size="sm" className="w-full mt-4">
        {t('jobs.card.viewDetails')}
      </BrutalButton>
    </div>
  );
}
